import {useCallback, useMemo} from 'react';
import type {Alignment, AstView, EnrichedAstNode, JustifyContent} from 'rendition-core';
import {useMultiNodeValue} from 'Hooks/useMultiNodeValue';

interface Args {
    selectedNodes: EnrichedAstNode[];
    view: AstView;
    handleRepairGroup: (view: AstView) => void;
}
const useChildAligmentToggles = ({selectedNodes, view, handleRepairGroup}: Args) => {
    const flexChildrenOfSelectedNodes = useMemo(
        () =>
            selectedNodes
                .map((n) => n.children)
                .flat()
                .filter((n) => !n.calculatedLayoutAbstraction!.absolutePosition),
        [selectedNodes]
    );

    const accessJustifyContent = useCallback((n: EnrichedAstNode) => n.calculatedLayoutAbstraction.justifyContent, []);
    const assignJustifyContent = useCallback((n: EnrichedAstNode, v: JustifyContent) => {
        n.inputLayoutAbstraction.justifyContent = v;
    }, []);

    const {updateNodes: setJustifyContents, value: justifyContent} = useMultiNodeValue(
        selectedNodes,
        accessJustifyContent,
        assignJustifyContent
    );

    const accessAlignSelf = useCallback((n: EnrichedAstNode) => n.calculatedLayoutAbstraction.alignSelf, []);
    const assignAlignSelf = useCallback((n: EnrichedAstNode, v: Alignment) => {
        n.inputLayoutAbstraction.alignSelf = v;
    }, []);

    const {updateNodes: setAlignSelfs, value: alignSelf} = useMultiNodeValue(
        flexChildrenOfSelectedNodes,
        accessAlignSelf,
        assignAlignSelf
    );

    return {justifyContent, alignSelf, setJustifyContents, setAlignSelfs, flexChildrenOfSelectedNodes};
};

export default useChildAligmentToggles;
